import React, { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const MAX = 150;
const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const MAX_PHOTOS = 3;
const FINANCIAL = new Set(['transaction', 'abono', 'fund']);
const TEXTS = {
  contract_termination: { title: 'Solicitar terminación de contrato', desc: 'Explica por qué debe terminarse este contrato. Quien tenga permiso para terminar contratos lo verá en Verificar → Solicitudes y lo atenderá.', ph: 'Ej. El inquilino entrega la unidad el 30 de este mes' },
  contract_renewal: { title: 'Solicitar renovación de contrato', desc: 'Propón las condiciones de la renovación. Quien tenga permiso para renovar contratos lo verá en Verificar → Solicitudes y lo atenderá.', ph: 'Ej. El inquilino acepta renovar un año con incremento del 5%' },
};
const ERROR_TEXT = {
  record: { title: 'Reportar error', desc: 'Describe el problema que detectaste en este registro. No se modifica nada: el revisor lo verá en Verificar → Solicitudes (tipo Error), lo atenderá y te avisará.', ph: 'Ej. El monto no coincide con el comprobante' },
  system: { title: 'Reportar error del sistema', desc: 'Describe la falla que encontraste (pantalla, botón, dato que no carga). Llegará al super admin en Verificar → Solicitudes (tipo Error).', ph: 'Ej. Al abrir Finanzas la tabla no carga' },
};

// Solicitar edición / eliminación / reportar error de un registro (Verificar → Solicitudes).
// target = { kind, id, summary, type?: 'delete' | 'error', module? }. Financieros (aprobados/bloqueados): el super admin lo reabre. Otros módulos: lo atiende y lo marca resuelto.
export const EditRequestDialog = ({ target, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rent, setRent] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const photoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { const urls = photos.map((f) => URL.createObjectURL(f)); setPreviews(urls); return () => urls.forEach((u) => URL.revokeObjectURL(u)); }, [photos]);
  const pickPhotos = (files) => {
    const next = [...photos];
    for (const f of Array.from(files || [])) {
      if (next.length >= MAX_PHOTOS) { toast.error(`Máximo ${MAX_PHOTOS} fotos por reporte`); break; }
      if (!PHOTO_TYPES.includes(f.type)) { toast.error(`${f.name}: la foto debe ser PNG, JPG o WEBP`); continue; }
      if (f.size > PHOTO_MAX_BYTES) { toast.error(`${f.name}: la foto no puede superar 10 MB`); continue; }
      next.push(f);
    }
    setPhotos(next);
  };
  useEffect(() => { if (target) { setReason(''); setEndDate(target.end_date || ''); setRent(target.monthly_rent != null ? String(target.monthly_rent) : ''); setPhotos([]); } }, [target]);
  const financial = FINANCIAL.has(target?.kind);
  const isDelete = target?.type === 'delete';
  const isError = target?.type === 'error';
  const t = isError ? ERROR_TEXT[target?.kind === 'system' ? 'system' : 'record'] : isDelete ? {
    title: 'Solicitar eliminación',
    desc: financial ? 'Este movimiento ya fue aprobado y está bloqueado. Explica por qué debe eliminarse; el super admin lo revisará y lo eliminará si procede.' : 'Explica por qué debe eliminarse este registro. El super admin lo verá en Verificar → Solicitudes, lo eliminará si procede y te avisará.',
    ph: financial ? 'Ej. El movimiento está duplicado' : 'Ej. Registro duplicado o creado por error',
  } : TEXTS[target?.kind];
  const isRenewal = target?.kind === 'contract_renewal';

  const submit = async () => {
    const r = reason.trim();
    if (r.length < 3) { toast.error('Describe el motivo (mínimo 3 caracteres)'); return; }
    setBusy(true);
    try {
      const proposal = isRenewal ? { end_date: endDate || null, monthly_rent: rent ? parseFloat(rent) : null } : undefined;
      const { data } = await api.post('/review/edit-requests', { kind: target.kind, id: target.id || '', request_type: isError ? 'error' : isDelete ? 'delete' : 'edit', reason: r, summary: target.summary || null, proposal, module: isError ? target.module || undefined : undefined });
      for (const f of (isError ? photos : [])) {
        const fd = new FormData(); fd.append('file', f);
        try { await api.post(`/review/edit-requests/${data.request_id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
        catch (e) { toast.warning(e?.response?.data?.detail || `El reporte se envió, pero la foto ${f.name} no se pudo subir`); }
      }
      toast.success(isError ? 'Reporte enviado: aparecerá en Verificar → Solicitudes como Error' : t && !isDelete ? 'Solicitud enviada: aparecerá en Verificar → Solicitudes' : 'Solicitud enviada: el super admin la verá en Verificar → Solicitudes');
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isError ? 'No se pudo enviar el reporte' : 'No se pudo enviar la solicitud'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-request-dialog">
        <DialogHeader>
          <DialogTitle>{t?.title || 'Solicitar edición'}</DialogTitle>
          <DialogDescription>
            {t?.desc || (financial
              ? 'Este movimiento ya fue aprobado y está bloqueado. Indica qué hay que corregir; el super admin lo reabrirá para que pueda editarse.'
              : 'Indica qué debe corregirse en este registro. El super admin lo verá en Verificar → Solicitudes, lo atenderá y te avisará.')}
          </DialogDescription>
        </DialogHeader>
        {target?.summary && <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="edit-request-summary">{target.summary}</p>}
        {isRenewal && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Nueva fecha de fin (propuesta)</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="edit-request-end-date" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Nueva renta mensual (propuesta)</label>
              <Input type="number" min="0" step="0.01" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="0.00" data-testid="edit-request-rent" />
            </div>
          </div>
        )}
        {isError && (
          <div className="space-y-1" data-testid="error-photo-field">
            <label className="text-xs font-medium text-slate-600">Fotos (opcional · hasta {MAX_PHOTOS})</label>
            <input ref={photoRef} type="file" multiple accept={PHOTO_TYPES.join(',')} className="hidden" onChange={(e) => { pickPhotos(e.target.files); e.target.value = ''; }} data-testid="error-photo-input" />
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="error-photo-list">
                {photos.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-50" data-testid={`error-photo-item-${i}`}>
                    <img src={previews[i]} alt={f.name} className="h-20 w-full object-cover" data-testid={`error-photo-preview-${i}`} />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5 text-slate-600 shadow hover:text-rose-600" aria-label="Quitar foto" data-testid={`error-photo-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                    <p className="truncate px-1.5 py-1 text-[11px] text-slate-600">{f.name}</p>
                  </div>
                ))}
              </div>
            )}
            {photos.length < MAX_PHOTOS && (
              <button type="button" onClick={() => photoRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 transition-colors hover:border-amber-400 hover:bg-amber-50 hover:text-amber-800" data-testid="error-photo-pick">
                <Camera className="h-4 w-4" />{photos.length ? `Agregar otra foto (${photos.length}/${MAX_PHOTOS})` : 'Agregar fotos o capturas de pantalla'}
              </button>
            )}
          </div>
        )}
        <div className="space-y-1">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, MAX))} rows={3} placeholder={t?.ph || (financial ? 'Ej. La cuenta correcta es Santander, no BBVA' : 'Ej. El teléfono del inquilino está desactualizado')} data-testid="edit-request-reason" />
          <p className="text-right text-[11px] text-slate-400">{reason.length}/{MAX}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="edit-request-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} className={isDelete ? 'bg-rose-600 hover:bg-rose-700' : isError ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#0d1e4c] hover:bg-[#15296a]'} data-testid="edit-request-submit">{busy ? 'Enviando…' : isError ? 'Enviar reporte' : 'Enviar solicitud'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRequestDialog;
